import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { vouchersApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { AuditTimeline } from '../../components/shared/AuditTimeline';
import { VoucherPrintTemplate } from '../../components/voucher/VoucherPrintTemplate';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  getPaymentMethodLabel,
  getPaymentTypeLabel,
} from '../../lib/utils';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  FileText,
  Download,
  Building2,
  User,
  Calendar,
  CreditCard,
  Banknote,
  Printer,
  PauseCircle,
  PlayCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

export const InternalAuditProcessPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [voucher, setVoucher] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [actionDialog, setActionDialog] = useState({ open: false, type: null });
  const [note, setNote] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [voucherData, logsData] = await Promise.all([
          vouchersApi.getById(id),
          vouchersApi.getAuditLogs(id),
        ]);
        setVoucher(voucherData);
        setAuditLogs(logsData);
      } catch (error) {
        toast.error('Voucher not found');
        navigate('/internal-audit/queue');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  const handleAction = async () => {
    setProcessing(true);
    try {
      let updated;
      if (actionDialog.type === 'approve') {
        updated = await vouchersApi.internalAuditApprove(id, note);
        toast.success('Voucher approved and forwarded to Accounts');
      } else if (actionDialog.type === 'reject') {
        updated = await vouchersApi.internalAuditReject(id, note);
        toast.success('Voucher rejected');
      } else if (actionDialog.type === 'hold') {
        updated = await vouchersApi.holdVoucher(id, note);
        toast.success('Voucher has been put on hold');
      } else if (actionDialog.type === 'release') {
        updated = await vouchersApi.releaseHold(id, note);
        toast.success('Voucher has been released from hold');
      }
      setVoucher(updated);
      const logs = await vouchersApi.getAuditLogs(id);
      setAuditLogs(logs);
      setActionDialog({ open: false, type: null });
      setNote('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!voucher) return null;

  const canTakeAction = voucher.status === 'MANAGER_APPROVED';

  return (
    <div className="space-y-6" data-testid="internal-audit-process-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/internal-audit/queue')}
            className="print:hidden"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-heading font-semibold tracking-tight">
              {voucher.voucherNo || 'Draft Voucher'}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <StatusBadge status={voucher.status} />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 print:hidden">
          <Button
            variant="outline"
            onClick={() => window.print()}
            data-testid="print-button"
          >
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
          {canTakeAction && (
            <>
              <Button
                variant="outline"
                onClick={() => setActionDialog({ open: true, type: 'reject' })}
                className="border-red-200 text-red-600 hover:bg-red-50"
                data-testid="reject-button"
              >
                <XCircle className="w-4 h-4 mr-2" /> Reject
              </Button>
              <Button
                variant="outline"
                onClick={() => setActionDialog({ open: true, type: 'hold' })}
                className="border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                data-testid="hold-button"
              >
                <PauseCircle className="w-4 h-4 mr-2" /> Hold
              </Button>
              <Button
                onClick={() => setActionDialog({ open: true, type: 'approve' })}
                className="bg-green-600 hover:bg-green-700"
                data-testid="approve-button"
              >
                <CheckCircle className="w-4 h-4 mr-2" /> Approve
              </Button>
            </>
          )}
          {voucher.status === 'HOLD' && (
            <Button
              onClick={() => setActionDialog({ open: true, type: 'release' })}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="release-hold-button"
            >
              <PlayCircle className="w-4 h-4 mr-2" /> Release Hold
            </Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#6a5da4]" /> Voucher Details
              </CardTitle>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-medium">{formatDate(voucher.date)}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Branch / Department</p>
                  <p className="font-medium">{voucher.branch} / {voucher.department}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center">
                  <User className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Requested By</p>
                  <p className="font-medium">{voucher.createdByName}</p>
                </div>
              </div>
              {(voucher.poNumber || voucher.poAmount) && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  {voucher.poNumber && (
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">PO Number</p>
                        <p className="font-medium font-mono">{voucher.poNumber}</p>
                      </div>
                    </div>
                  )}
                  {voucher.poAmount && (
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center">
                        <Banknote className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">PO Amount</p>
                        <p className="font-medium font-mono">{formatCurrency(voucher.poAmount, voucher.currency)}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Beneficiary */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <User className="w-4 h-4 text-[#6a5da4]" /> Beneficiary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-medium text-lg">{voucher.beneficiaryName}</p>
              {voucher.beneficiaryAddress && (
                <p className="text-muted-foreground mt-1">{voucher.beneficiaryAddress}</p>
              )}
            </CardContent>
          </Card>

          {/* Payment Details */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-[#6a5da4]" /> Payment Details
              </CardTitle>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Payment Method</p>
                <p className="font-medium">{getPaymentMethodLabel(voucher.paymentMethod)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Payment Type</p>
                <p className="font-medium">{getPaymentTypeLabel(voucher.paymentType)}</p>
              </div>
              {voucher.bankName && (
                <div>
                  <p className="text-sm text-muted-foreground">Bank Name</p>
                  <p className="font-medium">{voucher.bankName}</p>
                </div>
              )}
              {voucher.iban && (
                <div>
                  <p className="text-sm text-muted-foreground">IBAN</p>
                  <p className="font-medium font-mono text-sm">{voucher.iban}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Banknote className="w-4 h-4 text-[#6a5da4]" /> Line Items
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Document No.</TableHead>
                    <TableHead className="font-semibold">Description</TableHead>
                    <TableHead className="font-semibold text-right">Invoice Amount</TableHead>
                    <TableHead className="font-semibold text-right">Payable Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {voucher.items?.map((item, index) => (
                    <TableRow key={item.id || index}>
                      <TableCell className="font-mono">{item.invoiceNo || '-'}</TableCell>
                      <TableCell>{item.note || '-'}</TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(item.amount, item.currency || voucher.currency)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(item.payableAmount || 0, item.currency || voucher.currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-gray-50 font-semibold">
                    <TableCell colSpan={3} className="text-right">Total Payable Amount</TableCell>
                    <TableCell className="text-right font-mono text-lg">
                      {formatCurrency(voucher.amountTotal, voucher.currency)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Amount in Words */}
          {voucher.amountWords && (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground mb-1">Amount in Words</p>
                <p className="font-medium">{voucher.amountWords}</p>
              </CardContent>
            </Card>
          )}

          {/* Attachments */}
          {voucher.attachments?.length > 0 && (
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-base font-medium">Attachments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {voucher.attachments.map((attachment, index) => {
                    const [filename, originalName] = attachment.split('|');
                    return (
                      <a
                        key={index}
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${voucher.id}/${filename}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 rounded-lg border hover:bg-gray-50"
                      >
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span className="flex-1 truncate">{originalName || filename}</span>
                        <Download className="w-4 h-4 text-muted-foreground" />
                      </a>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Additional Attachments */}
          {voucher.additionalAttachments?.length > 0 && (
            <Card className="border-blue-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-base font-medium text-blue-700">Additional Attachments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {voucher.additionalAttachments.map((attachment, index) => {
                    const parts = attachment.split('|');
                    const filename = parts[0];
                    const originalName = parts[1] || filename;
                    const uploadedAt = parts[2];
                    return (
                      <a
                        key={index}
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${voucher.id}/additional/${filename}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 rounded-lg border border-blue-100 bg-blue-50 hover:bg-blue-100"
                      >
                        <FileText className="w-4 h-4 text-blue-600" />
                        <div className="flex-1">
                          <span className="truncate font-medium">{originalName}</span>
                          {uploadedAt && (
                            <p className="text-xs text-muted-foreground">
                              Uploaded: {formatDateTime(uploadedAt)}
                            </p>
                          )}
                        </div>
                        <Download className="w-4 h-4 text-blue-600" />
                      </a>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Amount Summary - Purple Card */}
          <Card className="bg-[#6a5da4] text-white">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <Banknote className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-sm opacity-80">Total Amount</p>
                  <p className="text-3xl font-mono font-semibold">
                    {formatCurrency(voucher.amountTotal, voucher.currency)}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/20">
                <div>
                  <p className="text-xs opacity-60 uppercase">Items</p>
                  <p className="font-semibold">{voucher.items?.length || 0}</p>
                </div>
                <div>
                  <p className="text-xs opacity-60 uppercase">Currency</p>
                  <p className="font-semibold">{voucher.currency}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Audit Timeline */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-medium">Approval History</CardTitle>
            </CardHeader>
            <CardContent>
              <AuditTimeline logs={auditLogs} />
            </CardContent>
          </Card>

          {/* Notes */}
          {(voucher.managerNote || voucher.internalAuditNote) && (
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-base font-medium">Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {voucher.managerNote && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Manager Note</p>
                    <p className="text-sm">{voucher.managerNote}</p>
                  </div>
                )}
                {voucher.internalAuditNote && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Audit Note</p>
                    <p className="text-sm">{voucher.internalAuditNote}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, type: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog.type === 'approve' ? 'Approve Voucher' : 
               actionDialog.type === 'hold' ? 'Put Voucher on Hold' :
               actionDialog.type === 'release' ? 'Release Voucher from Hold' : 'Reject Voucher'}
            </DialogTitle>
            <DialogDescription>
              {actionDialog.type === 'approve'
                ? 'This will forward the voucher to Accounts for final approval.'
                : actionDialog.type === 'hold'
                ? 'This voucher will be put on hold and the employee will be notified.'
                : actionDialog.type === 'release'
                ? 'This voucher will be released from hold and resume its previous workflow.'
                : 'This will reject the voucher and notify the employee.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="note">
                {actionDialog.type === 'approve' ? 'Note (optional)' : 
                 actionDialog.type === 'hold' ? 'Hold Reason (Required)' :
                 actionDialog.type === 'release' ? 'Release Note (optional)' : 'Note (Required)'}
              </Label>
              <Textarea
                id="note"
                placeholder={
                  actionDialog.type === 'approve' ? 'Add an optional note...' : 
                  actionDialog.type === 'hold' ? 'Please provide a reason for putting this voucher on hold...' :
                  actionDialog.type === 'release' ? 'Add any comments about the release...' :
                  'Please provide a reason for rejection...'
                }
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setActionDialog({ open: false, type: null })}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAction}
              disabled={processing || ((actionDialog.type === 'reject' || actionDialog.type === 'hold') && !note)}
              className={
                actionDialog.type === 'approve' ? 'bg-green-600 hover:bg-green-700' : 
                actionDialog.type === 'hold' ? 'bg-yellow-500 hover:bg-yellow-600' :
                actionDialog.type === 'release' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'
              }
              data-testid="confirm-action"
            >
              {processing ? 'Processing...' : 
               actionDialog.type === 'approve' ? 'Approve' : 
               actionDialog.type === 'hold' ? 'Put on Hold' :
               actionDialog.type === 'release' ? 'Release' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Template */}
      <VoucherPrintTemplate voucher={voucher} />
    </div>
  );
};

export default InternalAuditProcessPage;
